import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { productHubs, productHubSlugs } from "@/lib/productHubConfig";
import { cached } from "@/lib/ssrCache";

/** Hubs with fewer published articles than this are noindexed and hidden from listings/sitemap. */
export const MIN_HUB_ARTICLES = 3;

export interface HubArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  featured_image_alt: string | null;
  categories: string[] | null;
  tags: string[] | null;
  published_at: string | null;
  updated_at: string | null;
  product_rating: number | null;
}

/** All published articles matching a hub's taxonomy, newest first (filtered in the DB before limiting). */
export async function fetchHubArticles(hubSlug: string): Promise<HubArticle[]> {
  const config = productHubs[hubSlug];
  if (!config) return [];
  const { data, error } = await supabase
    .from("articles")
    .select("id, title, slug, excerpt, featured_image, featured_image_alt, categories, tags, published_at, updated_at, product_rating")
    .eq("status", "published")
    .or(
      config.articleCategories
        .flatMap((category) => [
          `category.ilike.${JSON.stringify(category)}`,
          `categories.cs.{${JSON.stringify(category)}}`,
          `tags.cs.{${JSON.stringify(category)}}`,
        ])
        .join(","),
    )
    .order("published_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  const cats = config.articleCategories.map((c) => c.toLowerCase());
  return ((data || []) as HubArticle[]).filter(
    (a) =>
      a.categories?.some((c) => cats.includes(c.toLowerCase())) ||
      a.tags?.some((t) => cats.includes(t.toLowerCase())),
  );
}

export const hubArticlesQueryOptions = (hubSlug: string) =>
  queryOptions({
    queryKey: ["product-hub-articles", hubSlug],
    queryFn: () => fetchHubArticles(hubSlug),
  });

export interface HubStat {
  count: number;
  lastmod: string | null;
}

/** Article count + newest article date per hub (cached 10 min per isolate). */
export function fetchHubStats(): Promise<Record<string, HubStat>> {
  return cached("hub-stats", 10 * 60 * 1000, async () => {
    // Read only taxonomy and dates once instead of fetching 100 full cards
    // separately for every hub. Paginate so older matching articles count too.
    type StatArticle = Pick<HubArticle, "id" | "categories" | "tags" | "updated_at" | "published_at">;
    const articles: StatArticle[] = [];
    const batchSize = 1000;
    for (let from = 0; ; from += batchSize) {
      const { data, error } = await supabase
        .from("articles")
        .select("id, categories, tags, updated_at, published_at")
        .eq("status", "published")
        .order("id", { ascending: true })
        .range(from, from + batchSize - 1);
      if (error) throw error; // Do not cache partial counts after a failed read.
      articles.push(...(data || []));
      if (!data || data.length < batchSize) break;
    }
    return Object.fromEntries(productHubSlugs.map((slug) => {
      // Array containment in the hub card query is case-sensitive. Count only
      // those same matches, not differently-cased tags the query cannot return.
      const categories = new Set(productHubs[slug].articleCategories);
      const matching = articles.filter((a) =>
        a.categories?.some((c) => categories.has(c)) ||
        a.tags?.some((t) => categories.has(t)),
      );
      const lastmod = matching.reduce<string | null>((max, a) => {
        const date = a.updated_at || a.published_at;
        return date && (!max || date > max) ? date : max;
      }, null);
      // Match the visible list's existing 100-card cap for indexing decisions.
      return [slug, { count: Math.min(matching.length, 100), lastmod }];
    }));
  });
}
